import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getWebsitePopup, WebsitePopup } from "../../apis/websitePopupApi";
import { resumeHeroBackgroundVideo } from "../../utils/heroVideo";

const STORAGE_KEY = "websitePopupSeen";
const LEGACY_STORAGE_KEY = "websitePopupSeenId";
const SEEN_TTL_MS = 30 * 60 * 1000; // 30 minutes

type SeenRecord = { id: string; expiresAt: number };

const getSeen = (): SeenRecord | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SeenRecord;
    if (!parsed?.id || typeof parsed.expiresAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

const markSeen = (id: string) => {
  const record: SeenRecord = {
    id,
    expiresAt: Date.now() + SEEN_TTL_MS,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  localStorage.removeItem(LEGACY_STORAGE_KEY);
};

const isStillSeen = (popupId: string): boolean => {
  const seen = getSeen();
  if (!seen) return false;
  if (Date.now() >= seen.expiresAt) {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
  return seen.id === popupId;
};

const WebsitePopupBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [popup, setPopup] = useState<WebsitePopup | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // Drop forever-seen legacy key so old dismissals don't stick
        localStorage.removeItem(LEGACY_STORAGE_KEY);

        const data = await getWebsitePopup();
        if (cancelled || !data?.imageUrl) return;

        if (isStillSeen(data.id)) return;

        setPopup(data);
        setOpen(true);
      } catch (error) {
        console.error("Failed to load website popup:", error);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClose = () => {
    if (popup?.id) {
      markSeen(popup.id);
    }
    setOpen(false);
    // Resume hero background video (paused while the dialog was open / after iOS gesture)
    resumeHeroBackgroundVideo();
  };

  if (!popup?.imageUrl) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: isMobile ? 1.5 : 2,
          width: "auto",
          maxWidth: "none",
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.65)" },
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <IconButton
          aria-label="Close popup"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: -12,
            right: -12,
            zIndex: 1,
            bgcolor: "common.white",
            color: "text.primary",
            boxShadow: 2,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            borderRadius: 2,
            bgcolor: "transparent",
            lineHeight: 0,
            display: "flex",
          }}
        >
          <Box
            component="img"
            src={popup.imageUrl}
            alt="Website announcement"
            sx={{
              display: "block",
              width: "auto",
              height: "auto",
              maxWidth: isMobile ? "calc(100vw - 32px)" : "min(720px, 90vw)",
              maxHeight: isMobile ? "75vh" : "85vh",
              borderRadius: 8,
            }}
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default WebsitePopupBanner;
